import {
    logDebug,
    logWarn,
    type Maybe,
    ReportableError,
    RequestError,
    StringParseError,
} from '@amzn/vinyl-util'
import { getDeviceInfo } from '@/device/device'
import { getAnonSessionId, getAuth, getAuthToken } from '@/auth/authRef'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { print } from 'graphql/language'
import type { GraphQLError } from 'graphql/error'
import { graphql } from 'gql.tada'
import { type SanitizedGql, sanitizeGql } from '@/util/gqlUtil'
import { getCacheManager } from '@/offline/offlineCacheManager'
import { getDeviceConfig } from '@/device/deviceConfig'
import { ctx } from '@/util/context'
import type { CacheEntryOptions } from '@amzn/vinyl-cache-manager'
import { blockIdleQueue } from '@/util/idleQueue'
import { sha256 } from '@/util/crypto'
import { instrument } from '@/core/perf'

//
// Amazon's API from following the https://dashboard.music.amazon.dev/quick-start
//

const GQL_ORIGIN = 'https://gql.music.amazon.dev/'

const { target } = ctx('gql')

/**
 * FireFly API keys
 * used to identify which client we're calling from
 * TODO: Replace these Dragonfly keys with provisioned Greenhornet keys
 *
 * Obtained via https://developer.amazon.com/apps-and-games/login-with-amazon
 */
const ApiKeys = {
    FIREFLY_IOS_API_KEY: 'amzn1.application.35feb30a280642f7a70e10bea35cda76',
    FIREFLY_ANDROID_API_KEY: 'amzn1.application.e6e1516750ea4b1c8f8820ed9dc848cf',
    FIREFLY_ANONYMOUS_DEBUG_API_KEY: 'amzn1.application.b124893c2c8f4ab2a3130fd015821477',
    FIREFLY_WEB_API_KEY: 'amzn1.application.e1dc16675f9f4c78b31927d5bfd5c229',
}

const unexpectedTokenRegex = /Unexpected token at (\d+)/

const graphQlWrapped = (...args: Parameters<typeof graphql>) => {
    try {
        return graphql(...args)
    } catch (error: any) {
        if (typeof error.message === 'string') {
            const match = unexpectedTokenRegex.exec(error.message)
            if (match) {
                const position = parseInt(match[1])
                throw new StringParseError(args[0], position, 'gql')
            }
        }
        throw error
    }
}
Object.assign(graphQlWrapped, {
    ...graphql,
})

export const gql: typeof graphql = graphQlWrapped as typeof graphql

export type GqlFetchOptions = {
    /**
     * If true, the response will be cached to persistence.
     */
    readonly cache?: Maybe<boolean>

    /**
     * If set, will retry once if the error is retryable.
     */
    readonly isRetryableError?: (error: GraphQLError) => boolean

    readonly cacheControlOverrides?: CacheEntryOptions['cacheControlOverrides']

    /**
     * If true, when the gql response contains errors, the fetch request will be rejected instead
     * of returning partial data.
     */
    readonly rejectPartialResponse?: boolean
}

const MAX_ATTEMPTS = 2

/**
 * Extracts the operation name from the query.
 */
const operationRegex = /query\s+([A-Za-z]+)/

export function gqlFetch<Result = any, Variables = any>(
    query: TypedDocumentNode<Result, Variables>,
    variables?: Variables,
    options?: GqlFetchOptions
): Promise<SanitizedGql<Result>> {
    const queryString = print(query)
    const [_, operationName] = queryString.match(operationRegex) ?? []
    return instrument('gqlFetch_' + operationName, async () => {
        const { deviceId } = getDeviceInfo()
        const cacheManager = getCacheManager()
        const { deviceTypeId } = getDeviceConfig()

        const headers: Record<string, any> = {
            'content-type': 'application/json',
            'x-amzn-device-id': deviceId,
            'x-amzn-device-type': deviceTypeId,
            'x-amzn-feature-flags': 'use-ups,api-cache',
            dnt: '1',
        }

        if (getAuth().isSignedIn()) {
            const oAuthToken = await getAuthToken()
            headers.authorization = oAuthToken.startsWith('Atna|')
                ? `AmznMusic ${btoa(
                      JSON.stringify({
                          access_token: oAuthToken,
                          deviceId,
                          deviceTypeId,
                      })
                  )}`
                : `Bearer ${oAuthToken}`
            headers['x-api-key'] = ApiKeys.FIREFLY_WEB_API_KEY
        } else {
            headers['x-api-key'] = ApiKeys.FIREFLY_ANONYMOUS_DEBUG_API_KEY
            headers['music-territory'] = 'US'
            headers['x-amzn-session-id'] = getAnonSessionId()
        }

        const sha256Hash = await sha256(queryString)
        const extensions = {
            persistedQuery: {
                version: 1,
                sha256Hash,
            },
        }

        let cacheKey: string | null = null
        if (options?.cache) {
            const variablesHash = await sha256(JSON.stringify(variables))
            cacheKey = operationName + '-' + sha256Hash + '-' + variablesHash
        }

        let attempts = 0
        let includeQuery = false

        while (++attempts <= MAX_ATTEMPTS) {
            const body: Record<string, any> = {
                variables,
                operationName,
                extensions,
            }
            if (includeQuery) {
                body.query = queryString
            }

            let response: Response

            try {
                // If a cacheKey is set in the options, use a cache-first strategy.
                response = await blockIdleQueue(
                    cacheManager.get(
                        new Request(GQL_ORIGIN, {
                            headers,
                            body: JSON.stringify(body),
                            method: 'POST',
                        }),
                        {
                            cacheKey,
                            cacheControlOverrides: {
                                // When there is a cacheId set, override the GQL cache headers to allow response
                                // caching. If no cacheId is set, this will have no effect and requests will be
                                // network-only.
                                noStore: false,
                                maxAge: 60 * 60,
                                staleWhileRevalidate: 7 * 24 * 60 * 60,
                                ...options?.cacheControlOverrides,
                            },
                            responseIsValid: async (response) => {
                                // Do not cache if there are errors, prefer stale cached responses if there are
                                // errors in the revalidated response.
                                const result = await response.clone().json()
                                return !result.errors
                            },
                        }
                    )
                )
            } catch (error) {
                if (error instanceof RequestError) {
                    if (error.response) {
                        const errorJson = await error.response.json()
                        throw new GqlError(errorJson.errors)
                    } else throw error
                } else {
                    throw error
                }
            }
            const result = await response.json()
            if (result.errors) {
                logWarn(target, printGqlErrors(result.errors))

                const persistErr = result.errors.find(
                    (e: GqlError) => e.message === 'PersistedQueryNotFound'
                )
                if (persistErr && !includeQuery) {
                    includeQuery = true
                    continue
                }

                const shouldRetry: boolean =
                    attempts < MAX_ATTEMPTS &&
                    options?.isRetryableError &&
                    result.errors.find((error: GraphQLError) => options.isRetryableError!(error))

                if (shouldRetry) logDebug(target, 'retrying')
                if (!shouldRetry) {
                    if (options?.rejectPartialResponse) {
                        throw new GqlError(result.errors)
                    }
                    // Not a retryable error, return the data that could be resolved.
                    return sanitizeGql(result.data)
                }
            } else {
                return sanitizeGql(result.data)
            }
        }
        throw new GqlError([])
    })()
}

export class GqlError extends ReportableError {
    get [Symbol.toStringTag](): string {
        return 'GqlError'
    }

    constructor(readonly errors: readonly GraphQLError[]) {
        super(`A service error occurred: ${printGqlErrors(errors)}`)
    }
}

function printGqlErrors(errors: readonly GraphQLError[]): string {
    return 'gql errors: ' + errors.map((error) => error.message).join('\n')
}
